import { dcos, dsin, wrapAngle, clamp, sign } from './fmath';
import type { TeamSpec } from './teams';
import type { Track } from './track';
import {
  ACCEL_TIME_SCALE, BRAKE_DECEL, CAR_RADIUS, COAST_DECEL, CRASH_NORMAL_MPH, DT,
  HIGH_SPEED_STEER_LOSS, MAX_YAW, REVERSE_ACCEL, REVERSE_MAX, SCRAPE_RESTITUTION,
  SCRAPE_TANGENT_KEEP, SURFACES, SurfaceId, YAW_FULL_SPEED, pxsFromMph,
} from './tuning';

/** One tick of player input, quantised so replays are exact (B12.2). */
export interface Input {
  /** -127 (full left) … 127 (full right). */
  steer: number;
  /** 0 … 127 */
  throttle: number;
  /** 0 … 127 */
  brake: number;
}

export const NO_INPUT: Input = { steer: 0, throttle: 0, brake: 0 };

export function quantise(steer: number, throttle: number, brake: number): Input {
  return {
    steer: Math.round(clamp(steer, -1, 1) * 127),
    throttle: Math.round(clamp(throttle, 0, 1) * 127),
    brake: Math.round(clamp(brake, 0, 1) * 127),
  };
}

export interface CarState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Heading in radians; 0 = facing +x (east), positive = clockwise on screen. */
  h: number;
  crashed: boolean;
  crashTicks: number;
  /** Touched a wall or parked car this tick. */
  contact: boolean;
  surface: SurfaceId;
  /** Tuning-streak top-speed bonus, mph. */
  bonusMph: number;
}

export interface CarPerf {
  vmax: number; // px/s
  accel: number; // px/s², engine force scale
  /** Daily modifiers (design.md B14.3): lateral grip multiplier (wet, worn tyres). */
  grip?: number;
  /** Daily modifiers: off-road penalty multiplier (0.4 = "grass is faster"). */
  offroad?: number;
}

/**
 * Engine model: dv/dt = A·throttle·(1 − u⁴), u = v / vmax.
 * A is chosen so 0→100 mph takes the team's quoted time × ACCEL_TIME_SCALE:
 *   t = (vmax / A) · ∫₀^u100 du / (1 − u⁴)
 * The integral is done with Simpson's rule (deterministic arithmetic only).
 */
export function carPerf(team: TeamSpec, bonusMph = 0): CarPerf {
  const vmax = pxsFromMph(team.topSpeedMph + bonusMph);
  const u100 = 100 / (team.topSpeedMph + bonusMph);
  const n = 64;
  const hstep = u100 / n;
  let sum = 0;
  for (let i = 0; i <= n; i++) {
    const u = i * hstep;
    const f = 1 / (1 - u * u * u * u);
    sum += i === 0 || i === n ? f : i % 2 === 1 ? 4 * f : 2 * f;
  }
  const integral = (sum * hstep) / 3;
  const t = team.zeroTo100 * ACCEL_TIME_SCALE;
  return { vmax, accel: (vmax * integral) / t };
}

export function newCar(x: number, y: number, h: number): CarState {
  return { x, y, vx: 0, vy: 0, h, crashed: false, crashTicks: 0, contact: false, surface: 'tarmac', bonusMph: 0 };
}

const CRASH_PXS = pxsFromMph(CRASH_NORMAL_MPH);

export interface StepResult {
  crashed: boolean;
}

/** Advance one car by one fixed tick. Mutates `car`. */
export function stepCar(car: CarState, perf: CarPerf, input: Input, track: Track): StepResult {
  car.contact = false;
  if (car.crashed) {
    car.crashTicks++;
    return { crashed: false };
  }

  const steer = input.steer / 127;
  const thr = input.throttle / 127;
  const brk = input.brake / 127;

  const fx = dcos(car.h);
  const fy = dsin(car.h);
  const rx = -fy;
  const ry = fx;
  let vf = car.vx * fx + car.vy * fy;
  let vl = car.vx * rx + car.vy * ry;

  const surfId = track.surfaceAt(car.x, car.y);
  car.surface = surfId;
  const surf = SURFACES[surfId === 'wall' ? 'gravel' : surfId];
  const off = perf.offroad !== undefined && (surfId === 'grass' || surfId === 'dust') ? perf.offroad : 1;
  const speedCap = off === 1 ? surf.speedCap : 1 - (1 - surf.speedCap) * off;
  const drag = off === 1 ? surf.drag : surf.drag * off;
  const grip = perf.grip === undefined ? surf.grip : surf.grip * perf.grip;
  const vcap = perf.vmax * speedCap;

  // Longitudinal.
  let a = 0;
  if (thr > 0) {
    if (vf < -1) {
      a += BRAKE_DECEL * thr; // throttle while rolling backwards = stop first
    } else {
      const u = clamp(vf / vcap, 0, 1);
      a += perf.accel * thr * (1 - u * u * u * u);
    }
  }
  if (brk > 0) {
    if (vf > 1) a -= BRAKE_DECEL * brk;
    else if (thr === 0 && vf > -REVERSE_MAX) a -= REVERSE_ACCEL * brk;
  }
  if (vf > vcap) a -= 2.5 * (vf - vcap); // e.g. arriving on grass too fast
  a -= drag * vf;
  const coasting = thr === 0 && brk === 0;

  const vfBefore = vf;
  vf += a * DT;
  if (coasting) {
    const c = COAST_DECEL * DT;
    vf = Math.abs(vf) <= c ? 0 : vf - sign(vf) * c;
  }
  // Braking never reverses the car by itself in one tick.
  if (brk > 0 && vfBefore > 1 && vf < 0) vf = 0;

  // Lateral grip.
  vl *= Math.max(0, 1 - grip * DT);

  // Steering (car-relative rotation, like the original).
  const speed = Math.abs(vf);
  const fade = Math.min(1, speed / YAW_FULL_SPEED);
  const hs = 1 - HIGH_SPEED_STEER_LOSS * Math.min(1, speed / perf.vmax);
  const dir = vf >= 0 ? 1 : -1;
  car.h = wrapAngle(car.h + steer * MAX_YAW * fade * hs * dir * DT);

  car.vx = fx * vf + rx * vl;
  car.vy = fy * vf + ry * vl;

  car.x += car.vx * DT;
  car.y += car.vy * DT;

  // Walls and parked cars.
  const c = track.collide(car.x, car.y, CAR_RADIUS);
  if (c) {
    car.contact = true;
    car.x = c.x;
    car.y = c.y;
    const vn = car.vx * c.nx + car.vy * c.ny; // < 0: moving into the wall
    if (-vn > CRASH_PXS) {
      car.crashed = true;
      car.crashTicks = 0;
      car.vx = 0;
      car.vy = 0;
      return { crashed: true };
    }
    if (vn < 0) {
      const tx = car.vx - vn * c.nx;
      const ty = car.vy - vn * c.ny;
      car.vx = tx * SCRAPE_TANGENT_KEEP - vn * SCRAPE_RESTITUTION * c.nx;
      car.vy = ty * SCRAPE_TANGENT_KEEP - vn * SCRAPE_RESTITUTION * c.ny;
    }
  }
  return { crashed: false };
}

export function speedOf(car: CarState): number {
  return Math.sqrt(car.vx * car.vx + car.vy * car.vy);
}
